import React, { useState } from 'react';
import classNames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Badge, CardBody, Collapse } from 'reactstrap';

interface Options {
    label: string;
    value: string;
}

export interface Permission {
    id: string;
    name: string;
    type: string;
    description?: string;
    options?: string | Options[];
    value: any;
    permissions?: Permission[];
}

export interface SubItem {
    id: string | number;
    name: string;
    children?: SubItem[];
    permissions: Permission[];
}

export interface MenuItem {
    id: string | number;
    name: string;
    children?: SubItem[];
    permissions: Permission[];
}

interface CreateInputs {
    role: string;
}

interface ReviewProps extends WithTranslation {
    createInputs: CreateInputs;
    accessData: MenuItem[];
    CreatePermission: () => void;
}

// Helpers
const isSelfAssigned = (perm: Permission): boolean =>
    perm.value !== null &&
    perm.value !== undefined &&
    perm.value.toString().toLowerCase() !== 'false' &&
    perm.value.toString().trim() !== '';

const getDisplayValue = (perm: Permission): string => {
    if (perm.type === 'dropdown') {
        let opts: Options[] = [];
        if (typeof perm.options === 'string') {
            try {
                opts = JSON.parse(perm.options);
            } catch {
                opts = [];
            }
        } else {
            opts = perm.options || [];
        }
        const found = opts.find(
            opt =>
                opt.value.toLowerCase() === perm.value.toString().toLowerCase() ||
                opt.label.toLowerCase() === perm.value.toString().toLowerCase()
        );
        return found ? found.label : perm.value.toString();
    } else if (perm.type === 'boolean') {
        return perm.value === true || perm.value === 'true' ? 'Enabled' : 'Disabled';
    }
    return perm.value.toString();
};

const ExpandablePermissionItem: React.FC<{
    permission: Permission;
    level?: number;
}> = ({ permission, level = 0 }) => {
    const [expanded, setExpanded] = useState(true);

    if (!isSelfAssigned(permission)) return null;

    const childPermissions = permission.permissions?.filter(isSelfAssigned) ?? [];
    const hasChildren = childPermissions.length > 0;

    const shouldRenderChildren =
        permission.type !== 'boolean' ||
        permission.value === true ||
        permission.value === 'true';

    return (
       <div className={`mb-2`} style={{ marginLeft: level * 20, borderRadius: 6 }}>
            <div
                className={classNames("d-flex justify-content-between align-items-center px-3 py-2", {
                    'border-start border-3 border-primary': level > 0,
                })}
                onClick={() => childPermissions.length > 0 && setExpanded(!expanded)}
                style={{ cursor: childPermissions.length > 0 ? 'pointer' : 'default' }}
            >
                <div className="d-flex align-items-start gap-2">
                    {childPermissions.length > 0 && (
                        <i
                            className={`mdi ${
                                expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'
                            } text-muted fs-5`}
                        />
                    )}
                    <div>
                        <div className="fw-semibold">{permission.name}</div>
                        {permission.description && (
                            <div className="text-muted small">{permission.description}</div>
                        )}
                    </div>
                </div>
                <span className="px-2 py-1">
                    <Badge color="primary"> {getDisplayValue(permission)} </Badge>
                </span>
            </div>
            {childPermissions.length > 0 && (
                <Collapse isOpen={expanded}>
                    {childPermissions.map(child => (
                        <ExpandablePermissionItem key={child.id} permission={child} level={level + 1} />
                    ))}
                </Collapse>
            )}
        </div>
    );
};

const ReviewMenuNode: React.FC<{
    node: MenuItem | SubItem;
    level?: number;
}> = ({ node, level = 0 }) => {
    const visiblePermissions = node.permissions?.filter(isSelfAssigned) || [];
    const visibleChildren =
        node.children?.filter(
            child =>
                (child.permissions && child.permissions.some(isSelfAssigned)) ||
                (child.children && child.children.length > 0)
        ) || [];

    if (visiblePermissions.length === 0 && visibleChildren.length === 0) return null;

    return (
        <div style={{ marginLeft: level * 20 }} className="mb-3">
            <div className="fw-bold text-primary mb-2">{node.name}</div>
            {visiblePermissions.map(perm => (
                <ExpandablePermissionItem
                    key={perm.id}
                    permission={perm}
                    level={level + 1}
                />
            ))}
            {visibleChildren.map(child => (
                <ReviewMenuNode key={child.id} node={child} level={level + 1} />
            ))}
        </div>
    );
};

const Review: React.FC<ReviewProps> = ({ createInputs, accessData, t }) => {
    return (
        <div className="p-4 m-5">
            <div className="text-center mb-4">
                <h3>
                    <span className="badge text-white px-3 py-2">
                        {createInputs.role}
                    </span>
                </h3>
            </div>
            <div className="text-center mb-3">
                <h4>{t('roles.review_permissions')}</h4>
            </div>
            {accessData?.length > 0 ? (
                accessData.map(node => (
                    <ReviewMenuNode key={node.id} node={node} />
                ))
            ) : (
                <div className="text-center text-muted">
                    <h5>{t('roles.no_permissions_assigned')}</h5>
                </div>
            )}
        </div>
    );
};

export default withTranslation()(Review);
